import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

// ─── Individual field renderer ────────────────────────────────────────────────

function FieldInput({ field, value, onChange, disabled }) {
  const cls = 'input';

  switch (field.type) {
    case 'text':
      return (
        <input
          type="text"
          className={cls}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
        />
      );

    case 'number':
      return (
        <input
          type="number"
          className={cls}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          placeholder={field.placeholder}
          min={0}
          disabled={disabled}
        />
      );

    case 'date':
      return (
        <input
          type="date"
          className={cls}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      );

    case 'textarea':
      return (
        <textarea
          className={cls}
          rows={field.rows ?? 4}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
        />
      );

    case 'select': {
      return (
        <select
          className={cls}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          {field.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    case 'checkboxes': {
      const checked = Array.isArray(value) ? value : [];
      return (
        <div className="space-y-2 pt-1">
          {field.options.map((opt) => (
            <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={checked.includes(opt.value)}
                onChange={(e) => {
                  if (e.target.checked) {
                    onChange([...checked, opt.value]);
                  } else {
                    onChange(checked.filter((v) => v !== opt.value));
                  }
                }}
                disabled={disabled}
                className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500 dark:border-neutral-600"
              />
              <span className="text-neutral-700 dark:text-neutral-300">{opt.label}</span>
            </label>
          ))}
        </div>
      );
    }

    case 'array': {
      const items = Array.isArray(value) ? value : [];
      return (
        <div className="space-y-3">
          {items.map((item, idx) => (
            <div
              key={idx}
              className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  {field.itemLabel} {idx + 1}
                </span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => onChange(items.filter((_, i) => i !== idx))}
                    className="text-neutral-400 hover:text-danger-600 dark:hover:text-danger-400 transition-colors"
                    aria-label={`Remove ${field.itemLabel} ${idx + 1}`}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {field.itemFields.map((itemField) => (
                  <div key={itemField.key}>
                    <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                      {itemField.label}
                    </label>
                    <FieldInput
                      field={itemField}
                      value={item[itemField.key]}
                      onChange={(val) => {
                        const updated = items.map((it, i) =>
                          i === idx ? { ...it, [itemField.key]: val } : it
                        );
                        onChange(updated);
                      }}
                      disabled={disabled}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {!disabled && (
            <button
              type="button"
              onClick={() => {
                const blank = {};
                field.itemFields.forEach((f) => { blank[f.key] = f.type === 'checkboxes' ? [] : ''; });
                onChange([...items, blank]);
              }}
              className="flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              {field.addLabel ?? 'Add Item'}
            </button>
          )}
        </div>
      );
    }

    default:
      return null;
  }
}

// ─── Section renderer ─────────────────────────────────────────────────────────

function SectionFields({ section, value, onChange, disabled }) {
  const cols = section.columns;

  // In a grid layout, textarea / checkboxes / array fields span all columns.
  const fullSpanTypes = new Set(['textarea', 'checkboxes', 'array']);
  const colSpanClass = cols ? `col-span-${cols}` : '';

  const wrapper = cols
    ? `grid grid-cols-${cols} gap-4`
    : 'space-y-4';

  return (
    <div className={wrapper}>
      {section.fields.map((field) => {
        const spanFull = cols && fullSpanTypes.has(field.type);
        return (
          <div key={field.key} className={spanFull ? colSpanClass : ''}>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              {field.label}
            </label>
            {field.hint && (
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">{field.hint}</p>
            )}
            <FieldInput
              field={field}
              value={value[field.key]}
              onChange={(val) => onChange({ ...value, [field.key]: val })}
              disabled={disabled}
            />
          </div>
        );
      })}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * Renders structured form fields for a given note type config.
 *
 * Props:
 *   config   – noteTypeConfig object (from noteTypeConfigs.js)
 *   value    – current data object { fieldKey: fieldValue, … }
 *   onChange – called with updated data object on any change
 *   disabled – if true, all inputs are read-only (for locked notes)
 */
export default function StructuredNoteForm({ config, value = {}, onChange, disabled = false }) {
  return (
    <div className="space-y-8">
      {config.sections.map((section, idx) => (
        <div key={idx}>
          <h3 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-4 pb-2 border-b border-neutral-200 dark:border-neutral-700">
            {section.title}
          </h3>
          <SectionFields
            section={section}
            value={value}
            onChange={onChange}
            disabled={disabled}
          />
        </div>
      ))}
    </div>
  );
}
